// ========================================
// ERROR LOGGING SERVICE
// ========================================
// Centralized logging for error tracking, monitoring, and debugging

import { ErrorContext, ErrorSeverity, StandardError } from '../types/errorTypes'

interface LoggingConfig {
  enableConsoleLogging: boolean
  enableLocalStorage: boolean
  enableRemoteLogging: boolean
  maxLocalStorageEntries: number
  logLevel: ErrorSeverity
  remoteEndpoint?: string
  apiKey?: string
}

interface LogEntry {
  id: string
  timestamp: string
  level: 'error' | 'warn' | 'info' | 'debug'
  message: string
  data?: unknown
  context?: Partial<ErrorContext>
}

class LoggingService {
  private config: LoggingConfig
  private localStorageKey = 'mntnd_error_logs'
  private sessionId: string
  private performanceMetrics: Map<string, number> = new Map()

  constructor(config: Partial<LoggingConfig> = {}) {
    this.config = {
      enableConsoleLogging: true,
      enableLocalStorage: true,
      enableRemoteLogging: false,
      maxLocalStorageEntries: 100,
      logLevel: ErrorSeverity.LOW,
      ...config,
    }

    this.sessionId = this.generateSessionId()
    this.initializeLogging()
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  private initializeLogging(): void {
    // Clean up old logs on initialization
    this.cleanupOldLogs()

    // Log session start
    this.info('Logging service initialized', {
      sessionId: this.sessionId,
      config: this.config,
    })
  }

  private shouldLog(severity: ErrorSeverity): boolean {
    const severityLevels = {
      [ErrorSeverity.LOW]: 0,
      [ErrorSeverity.MEDIUM]: 1,
      [ErrorSeverity.HIGH]: 2,
      [ErrorSeverity.CRITICAL]: 3,
    }

    return severityLevels[severity] >= severityLevels[this.config.logLevel]
  }

  private createLogEntry(
    level: 'error' | 'warn' | 'info' | 'debug',
    message: string,
    data?: unknown,
    context?: Partial<ErrorContext>
  ): LogEntry {
    return {
      id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
      context: {
        sessionId: this.sessionId,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent:
          typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        ...context,
      },
    }
  }

  private writeToConsole(entry: LogEntry): void {
    if (!this.config.enableConsoleLogging) return

    const timestamp = new Date(entry.timestamp).toLocaleTimeString()
    const prefix = `[${timestamp}] [${entry.level.toUpperCase()}]`

    switch (entry.level) {
      case 'error':
        console.error(prefix, entry.message, entry.data, entry.context)
        break
      case 'warn':
        console.warn(prefix, entry.message, entry.data, entry.context)
        break
      case 'info':
        console.info(prefix, entry.message, entry.data, entry.context)
        break
      case 'debug':
        console.debug(prefix, entry.message, entry.data, entry.context)
        break
    }
  }

  private writeToLocalStorage(entry: LogEntry): void {
    if (!this.config.enableLocalStorage || typeof window === 'undefined') return

    try {
      const existingLogs = this.getLocalStorageLogs()
      const updatedLogs = [entry, ...existingLogs].slice(
        0,
        this.config.maxLocalStorageEntries
      )

      localStorage.setItem(this.localStorageKey, JSON.stringify(updatedLogs))
    } catch (error) {
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        // Storage quota exceeded, clear old logs and retry
        this.clearLogs()
        try {
          // Store just the current entry
          localStorage.setItem(this.localStorageKey, JSON.stringify([entry]))
        } catch (retryError) {
          // If still failing, disable localStorage logging for this session
          console.warn(
            'localStorage quota exceeded, disabling localStorage logging:',
            retryError
          )
          this.config.enableLocalStorage = false
        }
      } else {
        console.warn('Failed to write to localStorage:', error)
      }
    }
  }

  private async writeToRemote(entry: LogEntry): Promise<void> {
    if (!this.config.enableRemoteLogging || !this.config.remoteEndpoint) return

    try {
      const response = await fetch(this.config.remoteEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.config.apiKey && {
            Authorization: `Bearer ${this.config.apiKey}`,
          }),
        },
        body: JSON.stringify(entry),
      })

      if (!response.ok) {
        throw new Error(`Remote logging failed: ${response.status}`)
      }
    } catch (error) {
      console.warn('Failed to send log to remote service:', error)
    }
  }

  private log(
    level: 'error' | 'warn' | 'info' | 'debug',
    message: string,
    data?: unknown,
    context?: Partial<ErrorContext>
  ): void {
    const entry = this.createLogEntry(level, message, data, context)

    this.writeToConsole(entry)
    this.writeToLocalStorage(entry)

    if (this.config.enableRemoteLogging) {
      this.writeToRemote(entry).catch(() => {
        // Silent fail for remote logging
      })
    }
  }

  // Public logging methods
  public error(
    message: string,
    data?: unknown,
    context?: Partial<ErrorContext>
  ): void {
    this.log('error', message, data, context)
  }

  public warn(
    message: string,
    data?: unknown,
    context?: Partial<ErrorContext>
  ): void {
    this.log('warn', message, data, context)
  }

  public info(
    message: string,
    data?: unknown,
    context?: Partial<ErrorContext>
  ): void {
    this.log('info', message, data, context)
  }

  public debug(
    message: string,
    data?: unknown,
    context?: Partial<ErrorContext>
  ): void {
    this.log('debug', message, data, context)
  }

  // Error-specific logging
  public logError(error: StandardError): void {
    if (!this.shouldLog(error.severity)) return

    this.error(
      `${error.category}/${error.code}: ${error.message}`,
      {
        error: {
          id: error.id,
          code: error.code,
          category: error.category,
          severity: error.severity,
          userMessage: error.userMessage,
          technicalMessage: error.technicalMessage,
          recoveryAction: error.recoveryAction,
          retryable: error.retryable,
          retryCount: error.retryCount,
          stack: error.stack,
        },
        originalError: error.originalError,
      },
      error.context
    )
  }

  public logErrorResolution(errorId: string, resolutionMethod: string): void {
    this.info(`Error resolved: ${errorId}`, {
      errorId,
      resolutionMethod,
      resolvedAt: new Date().toISOString(),
    })
  }

  // Performance monitoring
  public startPerformanceTimer(operationId: string): void {
    this.performanceMetrics.set(operationId, performance.now())
  }

  public endPerformanceTimer(operationId: string, details?: unknown): void {
    const startTime = this.performanceMetrics.get(operationId)
    if (startTime) {
      const duration = performance.now() - startTime
      this.performanceMetrics.delete(operationId)

      this.debug(`Performance: ${operationId}`, {
        duration: Math.round(duration),
        details,
      })
    }
  }

  // Log retrieval and management
  public getLocalStorageLogs(): LogEntry[] {
    if (typeof window === 'undefined') return []

    try {
      const logs = localStorage.getItem(this.localStorageKey)
      return logs ? JSON.parse(logs) : []
    } catch (error) {
      console.warn('Failed to retrieve logs from localStorage:', error)
      return []
    }
  }

  public getErrorLogs(): LogEntry[] {
    return this.getLocalStorageLogs().filter(log => log.level === 'error')
  }

  public getLogsByTimeRange(startTime: Date, endTime: Date): LogEntry[] {
    return this.getLocalStorageLogs().filter(log => {
      const logTime = new Date(log.timestamp)
      return logTime >= startTime && logTime <= endTime
    })
  }

  public clearLogs(): void {
    if (typeof window === 'undefined') return

    try {
      localStorage.removeItem(this.localStorageKey)
      this.info('Logs cleared')
    } catch (error) {
      console.warn('Failed to clear logs:', error)
    }
  }

  private cleanupOldLogs(): void {
    if (typeof window === 'undefined') return

    try {
      const logs = this.getLocalStorageLogs()
      const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)

      const recentLogs = logs.filter(
        log => new Date(log.timestamp) > threeDaysAgo
      )

      if (recentLogs.length !== logs.length) {
        localStorage.setItem(this.localStorageKey, JSON.stringify(recentLogs))
        this.info(
          `Cleaned up ${logs.length - recentLogs.length} old log entries`
        )
      }
    } catch (error) {
      console.warn('Failed to cleanup old logs:', error)
    }
  }

  // Analytics and reporting
  public getErrorStatistics(): {
    totalErrors: number
    errorsByCategory: Record<string, number>
    errorsBySeverity: Record<string, number>
    recentErrors: number
  } {
    const logs = this.getLocalStorageLogs()
    const errorLogs = logs.filter(log => log.level === 'error')
    const lastHour = new Date(Date.now() - 60 * 60 * 1000)

    const recentErrors = errorLogs.filter(
      log => new Date(log.timestamp) > lastHour
    ).length

    const errorsByCategory: Record<string, number> = {}
    const errorsBySeverity: Record<string, number> = {}

    errorLogs.forEach(log => {
      if (log.data && typeof log.data === 'object' && 'error' in log.data) {
        const errorData = log.data.error as Record<string, string>
        if (errorData.category) {
          errorsByCategory[errorData.category] =
            (errorsByCategory[errorData.category] || 0) + 1
        }
        if (errorData.severity) {
          errorsBySeverity[errorData.severity] =
            (errorsBySeverity[errorData.severity] || 0) + 1
        }
      }
    })

    return {
      totalErrors: errorLogs.length,
      errorsByCategory,
      errorsBySeverity,
      recentErrors,
    }
  }

  public exportLogs(): string {
    const logs = this.getLocalStorageLogs()
    return JSON.stringify(logs, null, 2)
  }

  // Configuration updates
  public updateConfig(newConfig: Partial<LoggingConfig>): void {
    this.config = { ...this.config, ...newConfig }
    this.info('Logging configuration updated', { newConfig })
  }

  public getConfig(): LoggingConfig {
    return { ...this.config }
  }
}

// Create singleton instance
export const loggingService = new LoggingService({
  enableConsoleLogging:
    import.meta.env.VITE_ENABLE_CONSOLE_LOGGING === 'true' ||
    import.meta.env.MODE === 'development',
  enableLocalStorage: true,
  // No remote endpoint: in dev, `utils/devConsoleBridge.ts` already forwards
  // every console line (including this service's console output) to
  // `logs/dev-console.log`, so posting here too would write each entry twice.
  enableRemoteLogging: false,
  remoteEndpoint: undefined,
  maxLocalStorageEntries: 200,
  logLevel:
    import.meta.env.VITE_LOG_LEVEL === 'error'
      ? ErrorSeverity.HIGH
      : ErrorSeverity.MEDIUM,
})

// Export for easy usage. Bind to the instance — destructuring these methods
// off `loggingService` drops their `this`, so the bare functions threw
// "Cannot read properties of undefined (reading 'log')" the moment any caller
// invoked them (latent bug surfaced by the push instrumentation, 2026-06-24).
export const logError = loggingService.error.bind(loggingService)
export const logWarn = loggingService.warn.bind(loggingService)
export const logInfo = loggingService.info.bind(loggingService)
export const logDebug = loggingService.debug.bind(loggingService)

export default loggingService
